import { useContext, useEffect, useState, createRef, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLingui } from '@lingui/react';
import { useCookies } from 'react-cookie';
import { translate } from '../../Service/PlaceholderTranslationService';
import { getClosest } from '../../Utils/utils';
import { FocusedNodeContext } from '../../Service/Context/FocusedNode';
import TextSelectionPopover from './TextSelectionPopover';
import { useMantineTheme } from '@mantine/core';
import { removeInlineStylesFromHtml } from '../../Utils/htmlUtils';

const PlaceholderInputField = ({
  htmlString: initialHtmlString,
  selectedValue,
  autoFocus,
  inlinePlaceholder,
  className,
  style,
  loading,
  big,
  error,
  name,
  finish,
  disabled,
  settings,
  onKeyDown,
  onInputChange,
  onBlur,
  onFocus,
  disableTextSelectionPopover,
  omitStylesOnPaste = false,
  preserveHtmlFormatting = false,
  ...rest
}) => {
  const [ref, setRef] = useState(createRef());
  const lastBlurHtmlRef = useRef(null);
  const { setFocusedNode, setListPosition } = useContext(FocusedNodeContext);
  const { colorScheme, colors } = useMantineTheme();

  const { i18n } = useLingui();
  const [locale, setLocale, removeLocale] = useCookies(['locale']);

  const getItems = () =>
    translate.toLocale(
      initialHtmlString,
      true,
      true,
      settings,
      i18n,
      locale,
      () => finish(ref.current.innerHTML),
      rest,
    );

  function focusNode(e) {
    const parentNode = getClosest(e.target, '.form-control');
    setFocusedNode(parentNode);
    setListPosition(rest.index);
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  }

  function handleBlur(e) {
    if (typeof onBlur === 'function') {
      onBlur(e);
      return;
    }

    if (typeof finish !== 'function') {
      return;
    }

    const newHtmlString = e.currentTarget.innerHTML;
    if (lastBlurHtmlRef.current === newHtmlString) {
      return;
    }

    lastBlurHtmlRef.current = newHtmlString;
    finish(newHtmlString);
  }

  const styles = {
    error: {
      borderColor: 'red',
    },
    input: {
      marginBottom: 0,
      height: '100%',
      minHeight: big ? 150 : 34,
      MozAppearance: 'textfield',
      WebkitAppearance: 'textfield',
      display: 'inline-block',
      backgroundColor: disabled ? '#f1f1f1' : colorScheme === 'dark' ? colors.dark[6] : 'white',
      color: colorScheme === 'dark' ? '#fff' : '#000',
      ...style,
    },
    label: {
      // color: 'red',
      fontWeight: 800,
    },
    inputContainer: {
      marginBottom: 9,
    },
    inputAndPlaceholderContainer: {
      display: 'flex',
    },
  };

  {
    /* fixes a bug in Firefox, that the placeholder cannot be deleted by backspace */
  }

  function onKeydownInFF(e) {
    /* if (e.key === "Backspace") {
            let selection = document.getSelection();

            // if caret is at the begining of the text node (0), remove previous element
            if (selection && selection.anchorOffset === 0) {
                if (selection.anchorNode.previousSibling != null && selection.anchorNode.previousSibling.parentNode != null) {
                    selection.anchorNode.previousSibling.parentNode.removeChild(selection.anchorNode.previousSibling)
                }
            }
        } */
  }

  function handlePaste(e) {
    if (!omitStylesOnPaste) {
      return;
    }

    const html = e.clipboardData.getData('text/html');
    if (!html) {
      return;
    }

    e.preventDefault();
    window.document.execCommand('insertHTML', false, removeInlineStylesFromHtml(html));
  }

  function setEditableRef(el) {
    if (!el || el === ref) {
      return;
    }

    const editableNode = el;

    if (preserveHtmlFormatting) {
      editableNode.innerHTML = initialHtmlString || '';
    }

    setRef(editableNode);
  }

  useEffect(() => {
    if (!preserveHtmlFormatting || !ref || typeof ref.innerHTML !== 'string' || document.activeElement === ref) {
      return;
    }

    const nextHtmlString = initialHtmlString || '';
    if (ref.innerHTML !== nextHtmlString) {
      ref.innerHTML = nextHtmlString;
      lastBlurHtmlRef.current = nextHtmlString;
    }
  }, [initialHtmlString, preserveHtmlFormatting, ref]);

  let classes = loading ? `loading form-control ${className}` : `form-control ${className}`;
  if (disabled) {
    classes += ' disabled';
  }

  const items = preserveHtmlFormatting ? null : getItems();

  const handleKeyDown = (e) => {
    // credit: https://stackoverflow.com/a/25397485/104380
    const isFF = !!navigator.userAgent.match(/firefox/i);
    const editableElm = document.querySelector('div');

    // listen to any key press
    if (isFF) editableElm.addEventListener('keydown', onKeydownInFF);

    if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    } else if (e.keyCode === 13 && e.shiftKey) {
    } else if (e.keyCode === 13) {
      // block enter
      e.preventDefault();
      window.document.execCommand('insertHTML', false, '<br><br>');
    }
  };

  const inputProps = {
    ref: setEditableRef,
    contentEditable: !disabled,
    suppressContentEditableWarning: true,
    onFocus: focusNode,
    onInput: onInputChange,
    onBlur: handleBlur,
    onPaste: handlePaste,
    placeholder: i18n._(inlinePlaceholder || ''),
    role: 'textbox',
    tabIndex: 0,
    name,
    style: error ? { ...styles.error, ...styles.input } : styles.input,
    className: classes,
    onKeyDown: handleKeyDown,
  };

  return (
    <>
      {!disableTextSelectionPopover && <TextSelectionPopover finish={finish} ref={ref} />}
      {preserveHtmlFormatting ? <div {...inputProps} /> : <div {...inputProps}>{items}</div>}
    </>
  );
};

PlaceholderInputField.propTypes = {
  htmlString: PropTypes.string,
  omitStylesOnPaste: PropTypes.bool,
  preserveHtmlFormatting: PropTypes.bool,
};

PlaceholderInputField.defaultProps = {
  omitStylesOnPaste: false,
  preserveHtmlFormatting: false,
};

export default PlaceholderInputField;
